"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { Bell, ArrowLeft, Pencil, Trash2, X, Eye, Link as LinkIcon } from "lucide-react";
import NotificationCenter from "./NotificationCenter";
import Tasks from "./Tasks";
import useFolder from "./useFolder";
import FilePreviewCard from "./FilePreviewCard"; // Import the new component
import { error as logError } from "../utils/logger";

const isHex = (c) => /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(c);

const colors = [
  { name: "Blue",   value: "#4299E1" },
  { name: "Orange", value: "#ED8936" },
  { name: "Purple", value: "#9F7AEA" },
  { name: "Teal",   value: "#38B2AC" },
  { name: "Pink",   value: "#ED64A6" },
  { name: "Indigo", value: "#6366F1" },
  { name: "Cyan",   value: "#06B6D4" },
  { name: "Slate",  value: "#64748B" },
];

export default function FolderScreen({
  selectedFolder,
  category,
  getButtonLabel,
  isShared,
  notifications,
  unreadCount,
  setNotifications,
  setUnreadCount,
  onBackToTasks,
  onFolderDeleted
}) {
  const [showNotifCenter, setShowNotifCenter] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false); // New state for file preview
  const notifRef = useRef(null);

  const { updateFolder, deleteFolder } = useFolder();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  const closeNotif = useCallback(() => setShowNotifCenter(false), []);

  // Close notification center on click outside
  useEffect(() => {
    if (!showNotifCenter) return;
    const onDocClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        closeNotif();
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") closeNotif();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [showNotifCenter, closeNotif]);

  // Defensive guard + skeleton
  if (!selectedFolder) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#4A5568]">Loading folder…</div>
      </div>
    );
  }

  const safeColor = isHex(selectedFolder.color) ? selectedFolder.color : "#4299E1";
  const badgeText = unreadCount > 99 ? "99+" : unreadCount;

// Avoid logging sensitive folder data

// Check if folder has a base file - try both possible field names
const hasBaseFile = selectedFolder.baseFileKey || selectedFolder.baseFile_attachment_key;

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <main className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Header */}
        <header className="flex-shrink-0 flex justify-between items-center p-8 pb-4">
          <div className="flex items-center">
            <button
              onClick={onBackToTasks}
              aria-label="Back to all tasks"
              className="mr-4 p-2 text-[#4A5568] hover:text-[#4299E1] hover:bg-[#EBF8FF] rounded-md transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center">
              <div
                className="w-1 h-8 rounded-full mr-3"
                style={{ backgroundColor: safeColor }}
                role="presentation"
              />
              <div className="flex items-center gap-3">
                <h2 className="text-4xl font-semibold text-[#2D3748]">
                  {selectedFolder.name}
                </h2>
                {/* Base File Indicator */}
                {hasBaseFile && (
                  <button
                    onClick={() => setShowFilePreview(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-[#4A5568] hover:text-[#4299E1] group"
                    title="Preview base file"
                  >
                    <LinkIcon 
                      className="w-5 h-5 group-hover:scale-110 transition-transform" 
                      style={{ color: safeColor }} 
                    />
                    <Eye className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Edit Folder Button */}
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-2 text-sm text-[#4A5568] hover:text-[#4299E1] hover:bg-[#EBF8FF] rounded-md transition-colors"
              title="Edit Folder"
            >
              <Pencil className="h-6 w-6" />
            </button>

            {/* Delete Folder Button */}
            <button
              onClick={() => setIsDeleting(true)}
              className="px-3 py-2 text-md rounded-lg hover:bg-red-50 hover:text-[#C53030] text-[#4A5568] transition-colors"
              title="Delete Folder"
            >
              <Trash2 className="h-6 w-6" />
            </button>

            {/* Notifications */}
            <div ref={notifRef} className="relative">
              <button
                onClick={() => setShowNotifCenter((s) => !s)}
                aria-label="Notifications"
                className="p-2 rounded-full relative hover:bg-[#EBF8FF] transition-colors duration-200"
              >
                <Bell className="h-6 w-6 text-[#2D3748] hover:text-[#4299E1]" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                    {badgeText}
                  </span>
                )}
              </button>

              {showNotifCenter && (
                <div className="absolute right-0 mt-2 w-[22rem] z-50">
                  <NotificationCenter
                    notifications={notifications}
                    setNotifications={setNotifications}
                    setUnreadCount={setUnreadCount}
                    closeCenter={closeNotif}
                  />
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Edit Folder Modal */}
        {isEditing && (
          <EditFolderModal
            initial={{
              name: selectedFolder.name,
              color: selectedFolder.color,
              baseFileLabel: selectedFolder.baseFileLabel || selectedFolder.baseFile_name || "",
              hasBaseFile: Boolean(selectedFolder.baseFileKey || selectedFolder.baseFile_attachment_key),
            }}
            saving={saving}
            onClose={() => setIsEditing(false)}
            onSave={async (values) => {
              setSaving(true);
              try {
                const updated = await updateFolder(selectedFolder.id, values);
                selectedFolder.name = updated.name;
                selectedFolder.color = updated.color;
                // Reflect base file changes from server response
                selectedFolder.baseFileKey = updated.baseFileKey;
                selectedFolder.baseFileLabel = updated.baseFileLabel;
                selectedFolder.baseFileUrl = updated.baseFileUrl;
                setIsEditing(false);
              } finally {
                setSaving(false);
              }
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {isDeleting && (
          <ConfirmDeleteModal
            folderName={selectedFolder.name}
            folderColor={safeColor}
            deleting={saving}
            onCancel={() => setIsDeleting(false)}
            onConfirm={async () => {
              setSaving(true);
              try {
                await deleteFolder(selectedFolder.id);
                if (onFolderDeleted) {
                  await onFolderDeleted(selectedFolder.id);
                }
                onBackToTasks();
                setIsDeleting(false);
              } catch (e) {
                logError(e);
                alert("Failed to delete folder. Please try again.");
              } finally {
                setSaving(false);
              }
            }}
          />
        )}

        {/* File Preview Modal */}
        {showFilePreview && hasBaseFile && (
         <FilePreviewCard
  folderId={selectedFolder.id}
  folderName={selectedFolder.name}
  folderColor={safeColor}
  onClose={() => setShowFilePreview(false)}
/>
        )}

        {/* Tasks */}
        <div className="flex-1 overflow-y-auto px-8 pb-8 scrollbar-hide min-h-0">
          <Tasks
            category={category}
            getButtonLabel={getButtonLabel}
            isShared={isShared}
            folderId={selectedFolder.id}
            forceFolderView={true}
            folders={[]}
          />
        </div>
      </main>
    </div>
  );
}

function EditFolderModal({ initial, onClose, onSave, saving }) {
  const [name, setName] = useState(initial.name ?? "");
  const [color, setColor] = useState(
    colors.find(c => c.value === (initial.color ?? ""))?.value || "#4299E1"
  );
  const [file, setFile] = useState(null);
  const [removeBaseFile, setRemoveBaseFile] = useState(false);
  const [baseFileLabel, setBaseFileLabel] = useState(initial.baseFileLabel || "");
  const fileInputRef = useRef(null);

  const canSave = name.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[440px] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Edit folder</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Base File Section */}
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Base file</span>
            {initial.hasBaseFile && !file && (
              <span className="text-xs text-gray-500 truncate max-w-[240px]" title={initial.baseFileLabel}>
                {initial.baseFileLabel || "(no label)"}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f);
                if (f) {
                  setRemoveBaseFile(false);
                  if (!baseFileLabel) setBaseFileLabel(f.name);
                }
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-sm"
            >
              {file ? "Change file" : (initial.hasBaseFile ? "Replace file" : "Attach file")}
            </button>
            {file && (
              <span className="text-xs text-gray-600 truncate max-w-[220px]" title={file.name}>{file.name}</span>
            )}
          </div>

          {(initial.hasBaseFile || file) && (
            <label className="block">
              <span className="text-sm text-gray-700">File label (optional)</span>
              <input
                value={baseFileLabel}
                onChange={(e) => setBaseFileLabel(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#4299E1]"
                placeholder="e.g., Base requirements.pdf"
              />
            </label>
          )}

          {initial.hasBaseFile && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={removeBaseFile}
                onChange={(e) => {
                  setRemoveBaseFile(e.target.checked);
                  if (e.target.checked) {
                    setFile(null);
                  }
                }}
              />
              Remove existing base file
            </label>
          )}
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="text-sm text-gray-700">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2
                         focus:outline-none focus:ring-2 focus:ring-[#4299E1]"
              placeholder="e.g., Research"
            />
          </label>

          <div className="block">
            <span className="text-sm text-gray-700">Color</span>
            <div className="mt-2 grid grid-cols-4 gap-3">
              <button
                type="button"
                aria-label="No color"
                onClick={() => setColor("")}
                aria-pressed={color === ""}
                className={`h-9 rounded-md border relative
                  ${color === "" ? "ring-2 ring-[#4299E1]" : "hover:border-gray-400"}
                `}
              >
                <div className="absolute inset-0 bg-[linear-gradient(45deg,_#f3f4f6_25%,_#e5e7eb_25%,_#e5e7eb_50%,_#f3f4f6_50%,_#f3f4f6_75%,_#e5e7eb_75%,_#e5e7eb_100%)] bg-[length:12px_12px] opacity-50 rounded-md" />
                <span className="sr-only">No color</span>
              </button>

              {colors.map((c) => {
                const selected = c.value === color;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    aria-label={c.name}
                    aria-pressed={selected}
                    className={`h-9 rounded-md border transition
                      ${selected ? "ring-2 ring-[#4299E1]" : "hover:border-gray-400"}`}
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                  />
                );
              })}
            </div>
            <div className="mt-3 h-1.5 rounded" style={{ backgroundColor: color || "#e5e7eb" }} />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200">
            Cancel
          </button>
          <button
            disabled={!canSave || saving}
            onClick={() => onSave({
              name: name.trim(),
              color: color.trim?.() ?? color,
              ...(file ? { baseFile: file, baseFileLabel: baseFileLabel } : {}),
              ...(removeBaseFile ? { removeBaseFile: true } : {}),
              ...(!file && !removeBaseFile && initial.hasBaseFile && baseFileLabel !== (initial.baseFileLabel || "")
                ? { baseFileLabel }
                : {}),
            })}
            className="px-4 py-2 rounded-md text-white bg-[#4299E1] hover:bg-[#3182CE] disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ folderName, folderColor, onCancel, onConfirm, deleting }) {
  const accent = folderColor && /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(folderColor)
    ? folderColor
    : "#e5e7eb";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" role="dialog" aria-modal="true">
      <div
        className="bg-white rounded-lg shadow-xl w-[440px] p-5 border-t-4 border-solid"
        style={{ borderTopColor: accent }}
      >
        <h3 className="text-lg font-semibold mb-2">Delete folder</h3>
        <p className="text-sm text-gray-700 mb-4">
          This action will permanently delete <b>{folderName}</b> and
          <b> all tasks inside this folder</b>. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-md text-white bg-[#FF3B30] hover:bg-red-600 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
      )
}
